<script setup lang="ts">
/**
 * 진단 패널 (BL-603) — 모니터링 store의 상세 상태를 펼쳐서 표시한다.
 *
 * StatusBar(Caster/Player)의 detail 영역에서 사용. role prop에 따라
 * caster/player 본인 link detail을 보여주고, 최근 연결 이벤트 이력을 함께 표시한다.
 */

import { computed } from 'vue'
import { useMonitoringStore } from '@/stores/monitoring.store'
import type { LinkDetail } from '@dronecast/shared'

const props = defineProps<{
  /** 표시할 역할 — 본인 link detail을 보여줌 */
  role: 'caster' | 'player'
  /** 표시 대상 link detail (StatusBar에서 전달) */
  detail: LinkDetail
}>()

const store = useMonitoringStore()

// ── 라벨 매핑 ────────────────────────────────────────────────

const sigLabels: Record<string, string> = {
  disconnected: '미연결', connecting: '연결 중', connected: '연결됨',
  reconnecting: '재연결 중', failed: '연결 실패',
}
const sdpLabels: Record<string, string> = {
  idle: '대기', in_progress: '진행 중', completed: '완료', failed: '실패',
}
const iceLabels: Record<string, string> = {
  idle: '대기', gathering: '수집 중', checking: '확인 중',
  connected: '연결됨', disconnected: '일시 끊김', failed: '실패',
}
const flowLabels: Record<string, string> = {
  idle: '대기', flowing: '수신 중', not_flowing: '중단',
}
const kmsLabels: Record<string, string> = {
  unknown: '미확인', healthy: '정상', degraded: '응답 지연', error: '연결 실패',
}

const segmentItems = computed(() => [
  { label: '시그널링', state: sigLabels[props.detail.signaling.state] || props.detail.signaling.state },
  { label: 'SDP 교환',  state: sdpLabels[props.detail.sdp.state] || props.detail.sdp.state },
  { label: 'ICE 연결',  state: iceLabels[props.detail.ice.state] || props.detail.ice.state },
  { label: '미디어 흐름', state: flowLabels[props.detail.mediaFlow.state] || props.detail.mediaFlow.state },
  { label: '미디어 서버', state: kmsLabels[props.detail.mediaServer.state] || props.detail.mediaServer.state },
])

function dotColor(state: string): string {
  const map: Record<string, string> = {
    '연결됨': '#28a745', '완료': '#28a745', '정상': '#28a745', '수신 중': '#28a745',
    '재연결 중': '#ffc107', '확인 중': '#ffc107', '수집 중': '#ffc107',
    '진행 중': '#ffc107', '응답 지연': '#ffc107', '일시 끊김': '#ffc107',
    '연결 실패': '#dc3545', '실패': '#dc3545', '중단': '#dc3545',
  }
  return map[state] || '#6c757d'
}

// ── WebRTC 통계 (mediaFlow 기반) ─────────────────────────────

const flow = computed(() => props.detail.mediaFlow)
const hasStats = computed(() =>
  flow.value.bitrate != null || flow.value.frameRate != null || flow.value.packetLoss != null || flow.value.jitter != null,
)

function formatBitrate(kbps?: number): string {
  if (kbps == null) return '-'
  return kbps >= 1000 ? `${(kbps / 1000).toFixed(1)} Mbps` : `${kbps.toFixed(0)} kbps`
}

// ── 이벤트 이력 (해당 role만) ────────────────────────────────

const myHistory = computed(() =>
  store.eventHistory.filter(e => e.role === props.role).slice(0, 10),
)

const segmentLabels: Record<string, string> = {
  signaling: '시그널링',
  sdp: 'SDP',
  ice: 'ICE',
  mediaFlow: '미디어흐름',
  mediaServer: '미디어서버',
  casterServerLink: 'Caster↔Server',
}

function formatTime(ts: number): string {
  const d = new Date(ts)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`
}
</script>

<template>
  <div class="diagnostic-panel">
    <!-- 구간별 상태 -->
    <table class="dp-table">
      <tbody>
        <tr v-for="item in segmentItems" :key="item.label">
          <td class="dp-label">
            <span class="dp-dot" :style="{ backgroundColor: dotColor(item.state) }"></span>
            {{ item.label }}
          </td>
          <td class="dp-state">{{ item.state }}</td>
        </tr>
      </tbody>
    </table>

    <!-- WebRTC 통계 -->
    <div v-if="hasStats" class="dp-stats">
      <span v-if="flow.bitrate != null" class="dp-stat">
        <span class="dp-stat-key">Bitrate</span>
        <span class="dp-stat-val">{{ formatBitrate(flow.bitrate) }}</span>
      </span>
      <span v-if="flow.frameRate != null" class="dp-stat">
        <span class="dp-stat-key">FPS</span>
        <span class="dp-stat-val">{{ flow.frameRate.toFixed(0) }}</span>
      </span>
      <span v-if="flow.packetLoss != null" class="dp-stat">
        <span class="dp-stat-key">Loss</span>
        <span class="dp-stat-val">{{ flow.packetLoss.toFixed(1) }}%</span>
      </span>
      <span v-if="flow.jitter != null" class="dp-stat">
        <span class="dp-stat-key">Jitter</span>
        <span class="dp-stat-val">{{ flow.jitter.toFixed(0) }}ms</span>
      </span>
    </div>

    <!-- 이벤트 이력 -->
    <div v-if="myHistory.length > 0" class="dp-history">
      <div class="dp-history-title">최근 이벤트</div>
      <ul class="dp-history-list">
        <li v-for="(e, i) in myHistory" :key="`${e.at}-${i}`">
          <span class="dp-history-time">{{ formatTime(e.at) }}</span>
          <span class="dp-history-segment">{{ segmentLabels[e.segment] || e.segment }}</span>
          <span class="dp-history-state">{{ e.state }}</span>
          <span v-if="e.message" class="dp-history-msg">{{ e.message }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped>
.diagnostic-panel {
  font-size: 12px;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 8px 12px;
}

.dp-table {
  width: 100%;
  border-collapse: collapse;
}
.dp-table td {
  padding: 3px 6px;
  border-bottom: 1px solid #f1f3f5;
}
.dp-table tr:last-child td { border-bottom: none; }

.dp-label {
  color: #495057;
  width: 40%;
}
.dp-state {
  color: #212529;
  font-weight: 500;
}

.dp-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
  vertical-align: middle;
}

.dp-stats {
  margin-top: 8px;
  padding-top: 6px;
  border-top: 1px dashed #dee2e6;
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
}
.dp-stat {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.dp-stat-key { color: #868e96; font-size: 11px; }
.dp-stat-val { font-weight: 600; font-variant-numeric: tabular-nums; }

.dp-history {
  margin-top: 8px;
  padding-top: 6px;
  border-top: 1px dashed #dee2e6;
}
.dp-history-title {
  color: #495057;
  font-weight: 600;
  margin-bottom: 4px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.dp-history-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 180px;
  overflow-y: auto;
}
.dp-history-list li {
  display: flex;
  gap: 8px;
  padding: 2px 0;
  font-size: 11px;
  color: #495057;
}
.dp-history-time { color: #868e96; font-variant-numeric: tabular-nums; flex-shrink: 0; }
.dp-history-segment { color: #495057; font-weight: 500; min-width: 70px; }
.dp-history-state { color: #212529; }
.dp-history-msg { color: #868e96; font-size: 10px; margin-left: auto; }
</style>
